import { useState, useEffect, useCallback } from 'react';
import fetchAPI from '../helper/axios';
import fetchAPICustom from '../helper/custom';
import { HomePageData, FeatureItem } from '../types/home';

// Cache storage
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchWithCache = async (endpoint: string, isCustom = false): Promise<any> => {
  const now = Date.now();
  const cached = cache.get(endpoint);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const data = isCustom ? await fetchAPICustom(endpoint) : await fetchAPI(endpoint);
  cache.set(endpoint, { data, timestamp: now });
  return data;
};

// Progressive loading with priorities
const API_ENDPOINTS = {
  critical: [
    { endpoint: 'webslider.php?id=2', isCustom: true }, // Desktop slider
    { endpoint: 'webslider.php?id=4', isCustom: true }, // Mobile slider
  ],
  important: [
    { endpoint: 'node/3', isCustom: false }, // Video section
    { endpoint: 'media/122/edit', isCustom: false }, // Video image
  ],
  secondary: [
    { endpoint: 'node/4', isCustom: false }, // About section
  ],
  features: [
    { endpoint: 'node/5', isCustom: false },
    { endpoint: 'node/6', isCustom: false },
    { endpoint: 'node/7', isCustom: false },
    { endpoint: 'node/8', isCustom: false },
    { endpoint: 'media/6/edit', isCustom: false },
    { endpoint: 'media/8/edit', isCustom: false },
    { endpoint: 'media/9/edit', isCustom: false },
    { endpoint: 'media/10/edit', isCustom: false },
    { endpoint: 'media/11/edit', isCustom: false },
      { endpoint: 'node/59', isCustom: false },
        { endpoint: 'node/60', isCustom: false },
     { endpoint: 'media/102/edit', isCustom: false },
      { endpoint: 'media/103/edit', isCustom: false },
  ],
  deferred: [
    { endpoint: 'node/9', isCustom: false },
    { endpoint: 'node/10', isCustom: false },
    { endpoint: 'node/11', isCustom: false },
    { endpoint: 'node/12', isCustom: false },
    { endpoint: 'media/16/edit', isCustom: false },
    { endpoint: 'node/13', isCustom: false },
    { endpoint: 'media/17/edit', isCustom: false },
    { endpoint: 'media/12/edit', isCustom: false },
  ]
};

export const useHomeData = () => {
  const [data, setData] = useState<HomePageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
    const [retry, setRetry] = useState(0); // for retry trigger
  const [loadingProgress, setLoadingProgress] = useState<{
    critical: boolean;
    important: boolean;
    secondary: boolean;
    features: boolean;
    deferred: boolean;
  }>({
    critical: true,
    important: false,
    secondary: false,
    features: false,
    deferred: false,
  });

  const loadSection = useCallback(async (endpoints: Array<{endpoint: string, isCustom: boolean}>, section: keyof typeof loadingProgress) => {
    try {
      const results = await Promise.all(
        endpoints.map(({ endpoint, isCustom }) => fetchWithCache(endpoint, isCustom))
      );
      return results;
      
    } catch (err) {
      console.error(`Error loading ${section} section:`, err);
      throw err;
    }
     // eslint-disable-next-line no-unreachable
   
  }, []);

  const updateData = useCallback((partialData: Partial<HomePageData>) => {
    setData(prev => prev ? { ...prev, ...partialData } : partialData as HomePageData);
  }, []);

  // Process slider data
  const processSliderData = (sliderData: any) => {
    if (!sliderData?.data?.slider_images) return sliderData;
    
    const processedImages = sliderData.data.slider_images.map((slide: any) => ({
      ...slide,
      slider_img_url: slide.slider_img_url ? slide.slider_img_url.replace("public://", "") : '',
      button_url: slide.button_url ? slide.button_url.replace("internal:", "") : ''
    }));
    
    return {
      ...sliderData,
      data: {
        ...sliderData.data,
        slider_images: processedImages
      }
    };
  };

  useEffect(() => {
    const loadDataProgressive = async () => {
      try {
        // Load critical data first (Sliders)
        const criticalResults = await loadSection(API_ENDPOINTS.critical, 'critical');
        updateData({
          hero: {
            desktopSlider: processSliderData(criticalResults[0])?.data || null,
            mobileSlider: processSliderData(criticalResults[1])?.data || null
          }
        });
        setLoadingProgress(prev => ({ ...prev, critical: false, important: true }));

        // Load important data (Video section)
        const importantResults = await loadSection(API_ENDPOINTS.important, 'important');
        updateData({
          video: {
            data: importantResults[0],
            image: importantResults[1]
          }
        });
        setLoadingProgress(prev => ({ ...prev, important: false, secondary: true }));

        // Load secondary data (About section)
        const secondaryResults = await loadSection(API_ENDPOINTS.secondary, 'secondary');
        updateData({
          about: {
            data: secondaryResults[0]
          }
        });
        setLoadingProgress(prev => ({ ...prev, secondary: false, features: true }));

        // Load features data
        const featuresResults = await loadSection(API_ENDPOINTS.features, 'features');
        updateData({
          features: {
            mainImage: featuresResults[4],
            items: [
              // { data: featuresResults[0], image: featuresResults[5], position: 'left' },
              // { data: featuresResults[1], image: featuresResults[8], position: 'right' },
              // { data: featuresResults[2], image: featuresResults[7], position: 'left' },
              // { data: featuresResults[3], image: featuresResults[6], position: 'right' }

              //   { data: featuresResults[0], image: featuresResults[5], position: 'left' },
              //   { data: featuresResults[1], image: featuresResults[6], position: 'right' },
              //  { data: featuresResults[3], image: featuresResults[8], position: 'left' },
              //   { data: featuresResults[9], image: featuresResults[11], position: 'right' },
              // { data: featuresResults[2], image: featuresResults[7], position: 'left' },
              //     { data: featuresResults[10], image: featuresResults[12], position: 'right' }
               { data: featuresResults[9], image: featuresResults[11], position: 'left' },
                 { data: featuresResults[10], image: featuresResults[12], position: 'right' },
                  { data: featuresResults[3], image: featuresResults[8], position: 'left' },
               { data: featuresResults[0], image: featuresResults[5], position: 'right' },
                { data: featuresResults[1], image: featuresResults[6], position: 'left' },
              
               
              { data: featuresResults[2], image: featuresResults[7], position: 'right' },
            ] as FeatureItem[]
          }
        });
        setLoadingProgress(prev => ({ ...prev, features: false, deferred: true }));

        // Load deferred data (Stats and About Gro)
        const deferredResults = await loadSection(API_ENDPOINTS.deferred, 'deferred');
        updateData({
          stats: {
            items: [
             // { data: deferredResults[0] },
            //  { data: deferredResults[1] },
              //Commented as per Cliesnt requeston 27/11/25 for Fuel Cards
             // { data: deferredResults[2] },
            //  { data: deferredResults[3] }
            ],
            backgroundImage: deferredResults[4]
          },
          aboutGro: {
            data: deferredResults[5],
            logo: deferredResults[6],
            backgroundImage: deferredResults[7]
          }
        });
        setLoadingProgress(prev => ({ ...prev, deferred: false }));
       
        setLoading(false);
      } catch (err: any) {
        setError(err.message);
         // Retry after 3 seconds
        setTimeout(() => setRetry((prev) => prev + 1), 10000);
        setLoading(false);
      }
    };

    loadDataProgressive();
  }, [loadSection, updateData,retry]);

  return { 
    data, 
    loading, 
    error, 
    loadingProgress,
    retry: () => {
      setLoading(true);
      setError(null);
      setLoadingProgress({
        critical: true,
        important: false,
        secondary: false,
        features: false,
        deferred: false,
      });
    }
  };
};
